const SW_PATH = "/sw.js";

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_PATH, {
      updateViaCache: "none",
    });
    await reg.update().catch(() => undefined);
    return reg;
  } catch {
    return null;
  }
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const result = await Notification.requestPermission();
  return result === "granted";
}

function msUntilNextTime(hour: number, minute = 0): number {
  const now = new Date();
  const next = new Date();
  next.setHours(hour, minute, 0, 0);
  if (next.getTime() <= now.getTime()) {
    next.setDate(next.getDate() + 1);
  }
  return next.getTime() - now.getTime();
}

function detectTaskTime(text: string): "morning" | "midday" | "evening" | "default" {
  const lower = text.toLowerCase();
  if (/breakfast|morning|সকাল|ব্রেকফাস্ট|নাস্তা|mid.morning|midmorning/.test(lower)) return "morning";
  if (/lunch|midday|দুপুর|mid.day/.test(lower)) return "midday";
  if (/dinner|night|evening|রাত|রাতের|বিকেল|afternoon/.test(lower)) return "evening";
  return "default";
}

const scheduleHandles: ReturnType<typeof setTimeout>[] = [];

export function clearScheduledReminders() {
  scheduleHandles.forEach(clearTimeout);
  scheduleHandles.length = 0;
}

export async function scheduleSmartReminders(tasks: { id: string; text: string; done: boolean; days_remaining: number }[]) {
  clearScheduledReminders();

  const pending = tasks.filter((t) => !t.done && t.days_remaining > 0);
  if (pending.length === 0) return;

  const granted = await requestNotificationPermission();
  if (!granted) return;

  const reg = await navigator.serviceWorker.ready.catch(() => null);

  const sendNotification = (title: string, body: string, tag: string) => {
    if (reg?.active) {
      reg.active.postMessage({ type: "SHOW_NOTIFICATION", title, body, tag, url: "/patient-tasks" });
    } else if (Notification.permission === "granted") {
      new Notification(title, { body, icon: "/logo.png", tag });
    }
  };

  const morningTasks = pending.filter((t) => detectTaskTime(t.text) === "morning");
  const middayTasks = pending.filter((t) => detectTaskTime(t.text) === "midday");
  const eveningTasks = pending.filter((t) => detectTaskTime(t.text) === "evening");
  const defaultTasks = pending.filter((t) => detectTaskTime(t.text) === "default");

  const allDefault = [...defaultTasks, ...morningTasks.length === 0 ? [] : [], ...middayTasks.length === 0 ? [] : []];

  const slots: { hour: number; minute: number; tasks: typeof pending; tag: string }[] = [
    { hour: 8, minute: 0, tasks: morningTasks.length > 0 ? morningTasks : [], tag: "morning-reminder" },
    { hour: 13, minute: 0, tasks: middayTasks.length > 0 ? middayTasks : defaultTasks, tag: "midday-reminder" },
    { hour: 19, minute: 0, tasks: eveningTasks.length > 0 ? eveningTasks : [], tag: "evening-reminder" },
    { hour: 9, minute: 30, tasks: allDefault.length > 0 ? allDefault : pending.slice(0, 3), tag: "daily-reminder" },
  ];

  slots.forEach(({ hour, minute, tasks: slotTasks, tag }) => {
    if (slotTasks.length === 0) return;

    const delay = msUntilNextTime(hour, minute);
    const sample = slotTasks[0].text;
    const count = slotTasks.length;

    const title = "Sebaco Health Reminder";
    const body =
      count === 1
        ? `📋 Task reminder: ${sample}`
        : `📋 You have ${count} health tasks pending — tap to check them`;

    const handle = setTimeout(() => {
      sendNotification(title, body, tag);
      const nextHandle = setInterval(() => sendNotification(title, body, tag), 24 * 60 * 60 * 1000);
      scheduleHandles.push(nextHandle);
    }, delay);

    scheduleHandles.push(handle);
  });
}
